<script setup>
    import { Field, useField } from 'vee-validate';
    import { computed, inject, onMounted, toRef, ref } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { string } from 'yup';
    import { useDccsField } from '../../composables/useDccsField';
    import { defaultFieldProps } from '../../helpers/defaultProps';
    import { useMutationObserver } from '@vueuse/core';

    const { t } = useI18n();

    const select = ref(null);
    const props = defineProps({
        ...defaultFieldProps,
        preselected: {
            type: String,
            required: false,
            default: null,
        },
        options: {
            type: [String, Array, Object],
            required: true,
            default: null,
        },
    });

    // eslint-disable-next-line no-unused-vars
    const emit = defineEmits(['update:modelValue']);

    const busy = inject('busy');

    const { fieldId, helpId, errorId, normalizedRequired, labelText, setRequired } = useDccsField(props);

    let validationRules = computed(() => {
        let validator = string();

        if (normalizedRequired.value === true) {
            validator = validator.required(t('form.validation.required', { name: props.label }));
        }

        return validator.nullable();
    });
    const optionsArray = computed(() => {
        let array = [];

        if (typeof props.options === 'string') {
            Object.values(props.options.split(';')).map(key => {
                let split = key.split(':');
                array[split[0]] = split[1];
            });

            return Object.entries(array).map(arr => ({
                key: arr[0],
                value: arr[1],
            }));
        }

        return props.options;
    });

    const refName = toRef(props, 'name');
    const { errorMessage, value } = useField(refName, validationRules);

    onMounted(() => {
        if (props.preselected !== null && optionsArray.value.some(o => o.key === props.preselected)) {
            value.value = props.preselected;
        }
    });

    function onClassChange(classAttrValue) {
        const classList = classAttrValue.split(' ');
        if (classList.includes('no-validate')) {
            setRequired(false);
            value.value = '';
        } else if (classList.includes('normal-validate')) {
            setRequired(props.required);
        }
    }

    useMutationObserver(
        select,
        mutations => {
            for (const m of mutations) {
                const newValue = m.target.getAttribute(m.attributeName);
                onClassChange(newValue, m.oldValue);
            }
        },
        { attributes: true, attributeOldValue: true, attributeFilter: ['class'] }
    );
</script>

<template>
    <Field v-slot="{ field, errors }" v-model="value" as="div" :name="name" :label="label" class="dccs-field">
        <label v-if="label && label.length" :class="['dccs-label', { 'has-error': errors.length }]" :for="fieldId">
            {{ labelText }}
        </label>

        <div class="dccs-control dccs-select">
            <select
                ref="select"
                v-bind="field"
                :id="fieldId"
                :name="name"
                :class="['dccs-input', { 'has-error': errors.length }]"
                :required="normalizedRequired"
                :disabled="busy"
                :aria-required="normalizedRequired"
                :aria-invalid="errors.length > 0"
                :aria-describedby="
                    [message ? helpId : null, errors.length ? errorId : null].filter(Boolean).join(' ')
                "
            >
                <option v-for="(option, index) in optionsArray" :key="`${label}-option-${index}`" :value="option.key">
                    {{ option.value }}
                </option>
            </select>
        </div>
        <p v-if="message && message.length" :id="helpId" class="dccs-help">
            {{ message }}
        </p>
        <p v-if="errors.length" :id="errorId" class="dccs-help is-danger" role="alert">
            {{ errorMessage || errors[0] }}
        </p>
    </Field>
</template>
